// src/admin/screens/loans/actions/ApproveLoanModal.tsx
// Flips a pending_approval loan to approved and stamps approved_at /
// approved_by_admin_id. The server enforces the status transition via the
// loan_status_transition trigger; this UI only drives the update + audit +
// toast + query invalidation.
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@admin/lib/supabase';
import { recordAudit } from '@admin/lib/audit';
import { useAuth } from '@admin/auth/useAuth';
import Modal from '@admin/components/Modal';
import Button from '@admin/components/Button';
import FormField from '@admin/components/FormField';
import Money from '@admin/components/Money';
import { useToast } from '@admin/components/ToastProvider';
import { translateError } from '@admin/utils/translateError';
import type { Ngwee } from '@/lib/types';
import type { LoanDetail } from '@admin/hooks/useLoan';

interface Props {
  loan: LoanDetail;
  onClose: (refresh: boolean) => void;
}

export default function ApproveLoanModal({ loan, onClose }: Props) {
  const qc = useQueryClient();
  const { admin } = useAuth();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');

  const submit = async () => {
    if (!admin) return;
    setBusy(true);
    try {
      const { error } = await supabase
        .from('loans')
        .update({
          status: 'approved',
          approved_by_admin_id: admin.id,
          approved_at: new Date().toISOString(),
        })
        .eq('id', loan.id);
      if (error) throw error;
      await recordAudit({
        action: 'loan.approve',
        target_type: 'loan',
        target_id: loan.id,
        payload: note ? { note } : {},
      });
      toast({ kind: 'success', msg: `${loan.loan_number} approved` });
      await qc.invalidateQueries({ queryKey: ['loan', loan.id] });
      onClose(true);
    } catch (err) {
      toast({ kind: 'error', msg: translateError(err as Error) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={() => onClose(false)} title={`Approve ${loan.loan_number}`}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <p>
          You&apos;re about to approve a loan of{' '}
          <strong>
            <Money amount={loan.principal_ngwee as Ngwee} />
          </strong>
          .
          {loan.total_repayable_ngwee != null && (
            <>
              {' '}
              Total repayable:{' '}
              <strong>
                <Money amount={loan.total_repayable_ngwee as Ngwee} />
              </strong>
              .
            </>
          )}
        </p>
        <FormField
          label="Internal note (optional)"
          htmlFor="approve-note"
          hint="Stored in the audit log for this action."
        >
          <textarea
            id="approve-note"
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </FormField>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={() => onClose(false)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={submit} loading={busy}>
            Approve
          </Button>
        </div>
      </div>
    </Modal>
  );
}
